
import { useState, useEffect } from "react";
import { usePageContext } from "@/hooks/usePageContext";
import { initializePrayerTimes } from "../utils";
import { usePrayerTimesState } from "./usePrayerTimesState";
import { useAutoSave } from "./useAutoSave";
import { useSaveContent } from "./useSaveContent";
import { useFormatConversion } from "./useFormatConversion";
import { usePrayerTimesHandlers } from "./usePrayerTimesHandlers";

interface UsePrayerTimesEditorProps {
  content: any;
  onContentChange: (field: string, value: any) => void;
}

export function usePrayerTimesEditor({ 
  content = {}, 
  onContentChange = () => {} 
}: UsePrayerTimesEditorProps) {
  const { currentUser } = usePageContext();
  
  // Debug logging
  useEffect(() => {
    console.log("PrayerTimesEditor initialized with content:", content);
  }, []);
  
  // Effect to initialize content when component mounts or content changes
  const [initialized, setInitialized] = useState(false);
  
  // Initialize prayer times with empty values first
  const {
    activeTab,
    setActiveTab,
    isLegacyFormat,
    setIsLegacyFormat,
    originalContentFormat,
    setOriginalContentFormat,
    prayerTimes,
    setPrayerTimes,
    blockTitle,
    setBlockTitle,
    animationSpeed,
    setAnimationSpeed,
    hasChanges,
    setHasChanges
  } = usePrayerTimesState({
    initialPrayerTimes: [],
    initialBlockTitle: "זמני תפילה",
    initialAnimationSpeed: 20,
    initialIsLegacyFormat: false,
    initialOriginalContentFormat: null
  });

  // Initialize content when the component mounts or content changes
  useEffect(() => {
    console.log("Initializing prayer times with content:", content);
    
    if (!content || Object.keys(content).length === 0) {
      console.log("Empty content provided, keeping current state");
      return;
    }
    
    const { 
      prayerTimes: initialPrayerTimes, 
      blockTitle: initialBlockTitle, 
      animationSpeed: initialAnimationSpeed,
      isLegacyFormat: initialLegacyFormat,
      originalContentFormat: initialOriginalContentFormat
    } = initializePrayerTimes(content);
    
    console.log("Initialized prayer times:", initialPrayerTimes);
    console.log("Initialized block title:", initialBlockTitle);
    
    setPrayerTimes(initialPrayerTimes);
    setBlockTitle(initialBlockTitle || "זמני תפילה");
    setAnimationSpeed(initialAnimationSpeed);
    setIsLegacyFormat(initialLegacyFormat);
    setOriginalContentFormat(initialOriginalContentFormat);
    setHasChanges(false);
    setInitialized(true);
  }, [content]);

  // Create save content function
  const { saveContent } = useSaveContent({
    prayerTimes,
    blockTitle,
    animationSpeed,
    isLegacyFormat,
    originalContentFormat,
    onContentChange,
    setHasChanges,
    content,
    currentUser
  });

  // Setup state updates without auto-save
  const { updateLocalState } = useAutoSave({
    prayerTimes,
    blockTitle,
    animationSpeed,
    saveContent,
    setHasChanges,
  });

  // Format conversion
  const { convertToModernFormat } = useFormatConversion({
    isLegacyFormat,
    setIsLegacyFormat,
    setHasChanges,
    saveContent,
  });

  // Event handlers for prayer times
  const {
    handleBlockTitleChange,
    handleAddPrayerTime,
    handleUpdatePrayerTime,
    handleRemovePrayerTime,
    handleAnimationSpeedChange,
    handleReorderPrayerTimes,
  } = usePrayerTimesHandlers({
    prayerTimes,
    blockTitle,
    setPrayerTimes,
    setBlockTitle,
    updateLocalState,
  });

  return {
    activeTab,
    setActiveTab,
    blockTitle,
    animationSpeed,
    prayerTimes,
    hasChanges,
    isLegacyFormat,
    saveContent,
    convertToModernFormat,
    handleBlockTitleChange,
    handleAddPrayerTime,
    handleUpdatePrayerTime,
    handleRemovePrayerTime,
    handleAnimationSpeedChange,
    handleReorderPrayerTimes,
  };
}
